import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ClipboardCopy, MessageSquareQuote, RefreshCw, Settings, Sparkles, Star, UserRoundPlus } from 'lucide-react';
import { api } from '../../lib/api';
import type { FeedbackRequestRecord, FeedbackResponseRecord, FeedbackSettingsRecord, FeedbackSummary, TestimonialRecord } from '../../lib/types';
import { Field, Input, Select, Textarea } from '../../components/ui/Field';
import Modal from '../../components/ui/Modal';
import QrCodeButton from '../../components/ui/QrCodeButton';
import PageContainer from '../../components/ui/PageContainer';
import PageHeader from '../../components/ui/PageHeader';

type Tab = 'requests' | 'responses' | 'testimonials';

function formatDate(value: Date | string | null | undefined) {
  if (!value) return 'Sin fecha';
  return new Date(value).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
}

function scoreLabel(type: 'csat' | 'nps', value: number) {
  return type === 'nps' ? `${value}/10 NPS` : `${value}/5 satisfacción`;
}

export default function ReputationPage() {
  const [tab, setTab] = useState<Tab>('requests');
  const [summary, setSummary] = useState<FeedbackSummary | null>(null);
  const [requests, setRequests] = useState<FeedbackRequestRecord[]>([]);
  const [responses, setResponses] = useState<FeedbackResponseRecord[]>([]);
  const [testimonials, setTestimonials] = useState<TestimonialRecord[]>([]);
  const [settings, setSettings] = useState<FeedbackSettingsRecord | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    channel: 'link' as 'link' | 'email' | 'whatsapp',
    scoreType: 'csat' as 'csat' | 'nps',
  });
  const [manualTestimonial, setManualTestimonial] = useState({
    quote: '',
    authorName: '',
    authorRole: '',
    authorLocation: '',
    ratingValue: '5',
  });

  async function load() {
    setLoading(true);
    try {
      const [summaryData, settingsData, requestRows, responseRows, testimonialRows] = await Promise.all([
        api.feedback.summary(),
        api.feedback.settings(),
        api.feedback.requests(),
        api.feedback.responses(),
        api.testimonials.list(),
      ]);
      setSummary(summaryData);
      setSettings(settingsData);
      setRequests(requestRows);
      setResponses(responseRows);
      setTestimonials(testimonialRows);
      setError('');
    } catch {
      setError('No se pudo cargar el módulo de reputación. Actualiza la página e inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!success) return;
    const timer = window.setTimeout(() => setSuccess(''), 3500);
    return () => window.clearTimeout(timer);
  }, [success]);

  const pendingRequests = useMemo(() => requests.filter((request) => ['pending', 'sent', 'opened'].includes(request.status)).length, [requests]);

  async function createManualRequest(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    try {
      const created = await api.feedback.createRequest(form);
      setSuccess(`Solicitud creada. Comparte ${created.shareUrl ?? created.sharePath} con el cliente.`);
      setForm({ customerName: '', customerEmail: '', customerPhone: '', channel: 'link', scoreType: 'csat' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear la solicitud.');
    } finally {
      setSaving(false);
    }
  }

  async function copySharePath(request: FeedbackRequestRecord) {
    try {
      await navigator.clipboard.writeText(request.shareUrl ?? request.sharePath);
      setSuccess('Enlace copiado.');
    } catch {
      setError('No se pudo copiar el enlace. Inténtalo de nuevo.');
    }
  }

  async function markAsSent(id: string) {
    try {
      await api.feedback.sendRequest(id);
      setSuccess('La solicitud quedó marcada como enviada.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo actualizar la solicitud.');
    }
  }

  async function reviewResponse(id: string, status: 'reviewed' | 'archived') {
    try {
      await api.feedback.reviewResponse(id, status);
      setSuccess(status === 'reviewed' ? 'Respuesta revisada.' : 'Respuesta archivada.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo actualizar la respuesta.');
    }
  }

  async function createTestimonialFromResponse(response: FeedbackResponseRecord) {
    try {
      await api.testimonials.create({ feedbackResponseId: response.id });
      setSuccess('El testimonio quedó listo para revisión.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el testimonio.');
    }
  }

  async function createManualTestimonial(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    try {
      await api.testimonials.create({
        quote: manualTestimonial.quote,
        authorName: manualTestimonial.authorName,
        authorRole: manualTestimonial.authorRole,
        authorLocation: manualTestimonial.authorLocation,
        ratingValue: manualTestimonial.ratingValue ? Number(manualTestimonial.ratingValue) : null,
      });
      setManualTestimonial({ quote: '', authorName: '', authorRole: '', authorLocation: '', ratingValue: '5' });
      setSuccess('Testimonio manual creado.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el testimonio.');
    } finally {
      setSaving(false);
    }
  }

  async function updateApproval(id: string, action: 'approve' | 'reject') {
    try {
      if (action === 'approve') await api.testimonials.approve(id);
      else await api.testimonials.reject(id);
      setSuccess(action === 'approve' ? 'Testimonio aprobado.' : 'Testimonio rechazado.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo actualizar el testimonio.');
    }
  }

  async function toggleFeatured(id: string, featured: boolean) {
    try {
      await api.testimonials.feature(id, featured);
      setSuccess(featured ? 'Testimonio destacado.' : 'Testimonio quitado de destacados.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo actualizar el destacado.');
    }
  }

  async function saveSettings(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!settings) return;
    setSaving(true);
    try {
      const saved = await api.feedback.updateSettings(settings);
      setSettings(saved);
      setSuccess('Los ajustes del formulario quedaron guardados.');
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron guardar los ajustes.');
    } finally {
      setSaving(false);
    }
  }

  const tabClass = (value: Tab) => `shrink-0 whitespace-nowrap px-3 py-2 text-sm rounded-md ${tab === value ? 'bg-ui-accent text-ui-bg' : 'text-ui-text-muted hover:bg-ui-text/[0.06]'}`;

  return (
    <PageContainer wide>
      <PageHeader
        icon={<MessageSquareQuote size={22} />}
        title="Reputación"
        description="Pide feedback, revisa respuestas y convierte buenas experiencias en testimonios para el sitio."
      >
        <button
          type="button"
          onClick={() => setSettingsOpen(true)}
          className="inline-flex items-center gap-2 rounded-md border border-ui-border px-3 py-2 text-sm text-ui-text hover:bg-ui-text/[0.05]"
        >
          <Settings size={15} /> Ajustes
        </button>
        <button
          type="button"
          onClick={() => load()}
          className="inline-flex items-center gap-2 rounded-md border border-ui-border px-3 py-2 text-sm text-ui-text hover:bg-ui-text/[0.05]"
        >
          <RefreshCw size={15} /> Actualizar
        </button>
      </PageHeader>

      {error && <p className="rounded-md bg-ui-danger/10 px-4 py-3 text-sm text-ui-danger">{error}</p>}
      {success && <p className="flex items-center gap-2 rounded-md border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-300"><CheckCircle2 size={16} />{success}</p>}

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Solicitudes activas" value={summary ? String(summary.requests.pending) : loading ? '…' : '0'} detail={`${pendingRequests} por responder o enviar`} />
        <StatCard title="Respuestas nuevas" value={summary ? String(summary.responses.newCount) : loading ? '…' : '0'} detail="Pendientes de revisión" />
        <StatCard title="Promedio" value={summary?.responses.avgScore != null ? String(summary.responses.avgScore) : '—'} detail="Promedio de satisfacción" />
        <StatCard title="Testimonios aprobados" value={summary ? String(summary.testimonials.approved) : loading ? '…' : '0'} detail={`${summary?.testimonials.featured ?? 0} destacados`} />
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.1fr_1.4fr]">
        <div className="min-w-0 rounded-xl border border-ui-border bg-ui-surface p-5">
          <div className="flex items-center gap-2 text-ui-text">
            <UserRoundPlus size={18} className="text-ui-accent" />
            <h2 className="font-medium">Nueva solicitud</h2>
          </div>
          <p className="mt-1 text-sm text-ui-text-muted">Úsala cuando quieras pedir feedback sin depender de una cita ya completada.</p>
          <form className="mt-4 space-y-4" onSubmit={createManualRequest}>
            <Field label="Nombre de la persona *">
              <Input value={form.customerName} onChange={(event) => setForm((prev) => ({ ...prev, customerName: event.target.value }))} required />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Correo">
                <Input type="email" value={form.customerEmail} onChange={(event) => setForm((prev) => ({ ...prev, customerEmail: event.target.value }))} />
              </Field>
              <Field label="Teléfono">
                <Input value={form.customerPhone} onChange={(event) => setForm((prev) => ({ ...prev, customerPhone: event.target.value }))} />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Canal previsto">
                <Select value={form.channel} onChange={(event) => setForm((prev) => ({ ...prev, channel: event.target.value as 'link' | 'email' | 'whatsapp' }))}>
                  <option value="link">Link directo</option>
                  <option value="email">Correo</option>
                  <option value="whatsapp">WhatsApp</option>
                </Select>
              </Field>
              <Field label="Tipo de score">
                <Select value={form.scoreType} onChange={(event) => setForm((prev) => ({ ...prev, scoreType: event.target.value as 'csat' | 'nps' }))}>
                  <option value="csat">Satisfacción 1 a 5</option>
                  <option value="nps">NPS 0 a 10</option>
                </Select>
              </Field>
            </div>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-ui-accent px-4 py-2 text-sm font-medium text-ui-bg hover:bg-ui-accent-hover disabled:opacity-50">
              <Sparkles size={15} /> {saving ? 'Creando…' : 'Crear solicitud'}
            </button>
          </form>
        </div>

        <div className="min-w-0 rounded-xl border border-ui-border bg-ui-surface p-5">
          <div className="flex overflow-x-auto [&::-webkit-scrollbar]:hidden gap-2 border-b border-ui-border pb-3">
            <button type="button" className={tabClass('requests')} onClick={() => setTab('requests')}>Solicitudes</button>
            <button type="button" className={tabClass('responses')} onClick={() => setTab('responses')}>Respuestas</button>
            <button type="button" className={tabClass('testimonials')} onClick={() => setTab('testimonials')}>Testimonios</button>
          </div>

          {tab === 'requests' && (
            <div className="mt-4 space-y-3">
              {requests.length === 0 && <EmptyState text="Todavía no hay solicitudes de feedback." />}
              {requests.map((request) => (
                <article key={request.id} className="rounded-lg border border-ui-border bg-ui-bg p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-medium text-ui-text">{request.customerName}</h3>
                        <StatusPill label={request.status} />
                      </div>
                      <p className="mt-1 text-sm text-ui-text-muted">
                        {request.serviceName ? `${request.serviceName}${request.staffName ? ` · ${request.staffName}` : ''}` : 'Solicitud manual'}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-x-2 text-sm text-ui-text-subtle">
                        <span>{request.customerEmail || 'Sin correo'}</span>
                        {request.customerPhone && <span>· {request.customerPhone}</span>}
                        <span>· {formatDate(request.createdAt)}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => copySharePath(request)} className="inline-flex items-center gap-2 rounded-md border border-ui-border px-3 py-2 text-sm text-ui-text hover:bg-ui-text/[0.05]">
                        <ClipboardCopy size={14} /> Copiar link
                      </button>
                      <QrCodeButton
                        value={request.shareUrl ?? request.sharePath}
                        label={request.customerName}
                        module="reputation"
                        refId={request.id}
                        fileName={`qr-${request.customerName}`}
                      />
                      {request.status !== 'responded' && request.status !== 'expired' && (
                        <button type="button" onClick={() => markAsSent(request.id)} className="inline-flex items-center gap-2 rounded-md bg-ui-accent px-3 py-2 text-sm font-medium text-ui-bg hover:bg-ui-accent-hover">
                          Marcar enviada
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 grid gap-2 text-sm text-ui-text-subtle sm:grid-cols-3">
                    <span>{request.scoreType === 'nps' ? 'Encuesta NPS' : 'Encuesta de satisfacción'}</span>
                    <span>Abierta: {request.openedAt ? formatDate(request.openedAt) : 'Todavía no'}</span>
                    <span>Respondida: {request.respondedAt ? formatDate(request.respondedAt) : 'Todavía no'}</span>
                  </div>
                </article>
              ))}
            </div>
          )}

          {tab === 'responses' && (
            <div className="mt-4 space-y-3">
              {responses.length === 0 && <EmptyState text="Todavía no hay respuestas recibidas." />}
              {responses.map((response) => (
                <article key={response.id} className="rounded-lg border border-ui-border bg-ui-bg p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-medium text-ui-text">{response.request.customerName}</h3>
                        <StatusPill label={response.status} />
                        <span className="rounded-full bg-ui-accent/10 px-2 py-1 text-sm font-medium text-ui-accent">{scoreLabel(response.scoreType, response.scoreValue)}</span>
                      </div>
                      <p className="mt-1 text-sm text-ui-text-muted">{response.comment || 'Sin comentario escrito.'}</p>
                      <div className="mt-2 flex flex-wrap gap-x-2 text-sm text-ui-text-subtle">
                        {response.publicName ? (
                          <>
                            <span>{response.publicName}</span>
                            {response.publicRole && <span>· {response.publicRole}</span>}
                          </>
                        ) : (
                          <span>Sin consentimiento público</span>
                        )}
                        <span>· {formatDate(response.createdAt)}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {response.status !== 'reviewed' && (
                        <button type="button" onClick={() => reviewResponse(response.id, 'reviewed')} className="rounded-md border border-ui-border px-3 py-2 text-sm text-ui-text hover:bg-ui-text/[0.05]">
                          Marcar revisada
                        </button>
                      )}
                      {response.consentToPublish && !response.testimonialId && response.comment && response.publicName && (
                        <button type="button" onClick={() => createTestimonialFromResponse(response)} className="rounded-md bg-ui-accent px-3 py-2 text-sm font-medium text-ui-bg hover:bg-ui-accent-hover">
                          Crear testimonio
                        </button>
                      )}
                      {response.status !== 'archived' && (
                        <button type="button" onClick={() => reviewResponse(response.id, 'archived')} className="rounded-md border border-ui-border px-3 py-2 text-sm text-ui-text-muted hover:text-ui-text hover:bg-ui-text/[0.05]">
                          Archivar
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {tab === 'testimonials' && (
            <div className="mt-4 space-y-5">
              <form onSubmit={createManualTestimonial} className="rounded-lg border border-ui-border bg-ui-bg p-4 space-y-4">
                <div className="flex items-center gap-2 text-ui-text">
                  <Star size={16} className="text-ui-accent" />
                  <h3 className="font-medium">Añadir testimonio manual</h3>
                </div>
                <Field label="Testimonio *">
                  <Textarea value={manualTestimonial.quote} onChange={(event) => setManualTestimonial((prev) => ({ ...prev, quote: event.target.value }))} required />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Nombre visible *">
                    <Input value={manualTestimonial.authorName} onChange={(event) => setManualTestimonial((prev) => ({ ...prev, authorName: event.target.value }))} required />
                  </Field>
                  <Field label="Calificación">
                    <Input type="number" min={1} max={5} value={manualTestimonial.ratingValue} onChange={(event) => setManualTestimonial((prev) => ({ ...prev, ratingValue: event.target.value }))} />
                  </Field>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Cargo o rol">
                    <Input value={manualTestimonial.authorRole} onChange={(event) => setManualTestimonial((prev) => ({ ...prev, authorRole: event.target.value }))} />
                  </Field>
                  <Field label="Ubicación">
                    <Input value={manualTestimonial.authorLocation} onChange={(event) => setManualTestimonial((prev) => ({ ...prev, authorLocation: event.target.value }))} />
                  </Field>
                </div>
                <button type="submit" disabled={saving} className="rounded-md bg-ui-accent px-4 py-2 text-sm font-medium text-ui-bg hover:bg-ui-accent-hover disabled:opacity-50">
                  Guardar testimonio
                </button>
              </form>

              <div className="space-y-3">
                {testimonials.length === 0 && <EmptyState text="Todavía no hay testimonios creados." />}
                {testimonials.map((testimonial) => (
                  <article key={testimonial.id} className="rounded-lg border border-ui-border bg-ui-bg p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-medium text-ui-text">{testimonial.authorName}</h3>
                          <StatusPill label={testimonial.approvalStatus} />
                          {testimonial.featured && <span className="rounded-full bg-ui-accent/10 px-2 py-1 text-sm font-medium text-ui-accent">Destacado</span>}
                        </div>
                        <p className="mt-2 text-sm text-ui-text">“{testimonial.quote}”</p>
                        <div className="mt-2 flex flex-wrap gap-x-2 text-sm text-ui-text-subtle">
                          <span>{testimonial.authorRole || 'Sin cargo'}</span>
                          {testimonial.authorLocation && <span>· {testimonial.authorLocation}</span>}
                          {testimonial.ratingValue != null && <span>· {testimonial.ratingValue}/5</span>}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {testimonial.approvalStatus !== 'approved' && (
                          <button type="button" onClick={() => updateApproval(testimonial.id, 'approve')} className="rounded-md bg-ui-accent px-3 py-2 text-sm font-medium text-ui-bg hover:bg-ui-accent-hover">
                            Aprobar
                          </button>
                        )}
                        {testimonial.approvalStatus !== 'rejected' && (
                          <button type="button" onClick={() => updateApproval(testimonial.id, 'reject')} className="rounded-md border border-ui-border px-3 py-2 text-sm text-ui-text hover:bg-ui-text/[0.05]">
                            Rechazar
                          </button>
                        )}
                        <label className="inline-flex items-center gap-2 rounded-md border border-ui-border px-3 py-2 text-sm text-ui-text">
                          <input type="checkbox" className="h-4 w-4 accent-ui-accent" checked={testimonial.featured} onChange={(event) => toggleFeatured(testimonial.id, event.target.checked)} />
                          Destacar
                        </label>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}

        </div>
      </section>

      {settingsOpen && settings && (
        <Modal title="Ajustes de feedback" onClose={() => setSettingsOpen(false)} wide>
          <form className="space-y-5 pb-5" onSubmit={saveSettings}>
            <div className="rounded-lg border border-ui-border bg-ui-bg p-4 space-y-4">
              <h3 className="font-medium text-ui-text">Encabezado y tono</h3>
              <Field label="Título principal">
                <Input value={settings.title} onChange={(event) => setSettings((prev) => prev ? { ...prev, title: event.target.value } : prev)} />
              </Field>
              <Field label="Texto introductorio">
                <Textarea value={settings.intro} onChange={(event) => setSettings((prev) => prev ? { ...prev, intro: event.target.value } : prev)} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Pregunta para satisfacción 1 a 5">
                  <Input value={settings.scoreQuestionCsat} onChange={(event) => setSettings((prev) => prev ? { ...prev, scoreQuestionCsat: event.target.value } : prev)} />
                </Field>
                <Field label="Pregunta para NPS 0 a 10">
                  <Input value={settings.scoreQuestionNps} onChange={(event) => setSettings((prev) => prev ? { ...prev, scoreQuestionNps: event.target.value } : prev)} />
                </Field>
              </div>
            </div>

            <div className="rounded-lg border border-ui-border bg-ui-bg p-4 space-y-4">
              <h3 className="font-medium text-ui-text">Selección guiada por score</h3>
              <p className="text-sm text-ui-text-muted">Escribe una opción por línea. El formulario mostrará estos chips según si la respuesta fue alta, media o baja.</p>
              <div className="grid gap-4 lg:grid-cols-3">
                <Field label="Chips para score alto">
                  <Textarea value={settings.positiveChips} onChange={(event) => setSettings((prev) => prev ? { ...prev, positiveChips: event.target.value } : prev)} rows={6} />
                </Field>
                <Field label="Chips para score medio">
                  <Textarea value={settings.neutralChips} onChange={(event) => setSettings((prev) => prev ? { ...prev, neutralChips: event.target.value } : prev)} rows={6} />
                </Field>
                <Field label="Chips para score bajo">
                  <Textarea value={settings.negativeChips} onChange={(event) => setSettings((prev) => prev ? { ...prev, negativeChips: event.target.value } : prev)} rows={6} />
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Invitar a testimonio desde satisfacción">
                  <Select value={String(settings.testimonialMinScoreCsat)} onChange={(event) => setSettings((prev) => prev ? { ...prev, testimonialMinScoreCsat: Number(event.target.value) } : prev)}>
                    <option value="1">Desde 1/5</option>
                    <option value="2">Desde 2/5</option>
                    <option value="3">Desde 3/5</option>
                    <option value="4">Desde 4/5</option>
                    <option value="5">Solo 5/5</option>
                  </Select>
                </Field>
                <Field label="Invitar a testimonio desde NPS">
                  <Select value={String(settings.testimonialMinScoreNps)} onChange={(event) => setSettings((prev) => prev ? { ...prev, testimonialMinScoreNps: Number(event.target.value) } : prev)}>
                    <option value="7">Desde 7/10</option>
                    <option value="8">Desde 8/10</option>
                    <option value="9">Desde 9/10</option>
                    <option value="10">Solo 10/10</option>
                  </Select>
                </Field>
              </div>
            </div>

            <div className="rounded-lg border border-ui-border bg-ui-bg p-4 space-y-4">
              <h3 className="font-medium text-ui-text">Campos del formulario</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Label del comentario">
                  <Input value={settings.commentLabel} onChange={(event) => setSettings((prev) => prev ? { ...prev, commentLabel: event.target.value } : prev)} />
                </Field>
                <Field label="Placeholder del comentario">
                  <Input value={settings.commentPlaceholder} onChange={(event) => setSettings((prev) => prev ? { ...prev, commentPlaceholder: event.target.value } : prev)} />
                </Field>
              </div>
              <Field label="Texto de autorización para testimonio">
                <Textarea value={settings.consentLabel} onChange={(event) => setSettings((prev) => prev ? { ...prev, consentLabel: event.target.value } : prev)} />
              </Field>
              <Field label="Autorización activada por default">
                <Select value={settings.consentDefaultChecked ? 'true' : 'false'} onChange={(event) => setSettings((prev) => prev ? { ...prev, consentDefaultChecked: event.target.value === 'true' } : prev)}>
                  <option value="true">Sí, activada</option>
                  <option value="false">No, desactivada</option>
                </Select>
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Label de nombre visible">
                  <Input value={settings.publicNameLabel} onChange={(event) => setSettings((prev) => prev ? { ...prev, publicNameLabel: event.target.value } : prev)} />
                </Field>
                <Field label="Placeholder de nombre visible">
                  <Input value={settings.publicNamePlaceholder} onChange={(event) => setSettings((prev) => prev ? { ...prev, publicNamePlaceholder: event.target.value } : prev)} />
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Label de cargo o relación">
                  <Input value={settings.publicRoleLabel} onChange={(event) => setSettings((prev) => prev ? { ...prev, publicRoleLabel: event.target.value } : prev)} />
                </Field>
                <Field label="Placeholder de cargo o relación">
                  <Input value={settings.publicRolePlaceholder} onChange={(event) => setSettings((prev) => prev ? { ...prev, publicRolePlaceholder: event.target.value } : prev)} />
                </Field>
              </div>
              <Field label="Mostrar campo de cargo o relación">
                <Select value={settings.publicRoleMode} onChange={(event) => setSettings((prev) => prev ? { ...prev, publicRoleMode: event.target.value as 'hidden' | 'optional' } : prev)}>
                  <option value="optional">Opcional</option>
                  <option value="hidden">Oculto</option>
                </Select>
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Label de ubicación">
                  <Input value={settings.publicLocationLabel} onChange={(event) => setSettings((prev) => prev ? { ...prev, publicLocationLabel: event.target.value } : prev)} />
                </Field>
                <Field label="Placeholder de ubicación">
                  <Input value={settings.publicLocationPlaceholder} onChange={(event) => setSettings((prev) => prev ? { ...prev, publicLocationPlaceholder: event.target.value } : prev)} />
                </Field>
              </div>
              <Field label="Mostrar campo de ubicación">
                <Select value={settings.publicLocationMode} onChange={(event) => setSettings((prev) => prev ? { ...prev, publicLocationMode: event.target.value as 'hidden' | 'optional' } : prev)}>
                  <option value="optional">Opcional</option>
                  <option value="hidden">Oculto</option>
                </Select>
              </Field>
              <Field label="Texto del botón">
                <Input value={settings.submitLabel} onChange={(event) => setSettings((prev) => prev ? { ...prev, submitLabel: event.target.value } : prev)} />
              </Field>
            </div>

            <div className="rounded-lg border border-ui-border bg-ui-bg p-4 space-y-4">
              <h3 className="font-medium text-ui-text">Mensajes del flujo</h3>
              <Field label="Mensaje cuando la encuesta está lista">
                <Input value={settings.readyMessage} onChange={(event) => setSettings((prev) => prev ? { ...prev, readyMessage: event.target.value } : prev)} />
              </Field>
              <Field label="Mensaje mientras se envía">
                <Input value={settings.submittingMessage} onChange={(event) => setSettings((prev) => prev ? { ...prev, submittingMessage: event.target.value } : prev)} />
              </Field>
              <Field label="Mensaje de agradecimiento">
                <Input value={settings.successMessage} onChange={(event) => setSettings((prev) => prev ? { ...prev, successMessage: event.target.value } : prev)} />
              </Field>
              <Field label="Mensaje cuando ya respondieron">
                <Input value={settings.alreadyRespondedMessage} onChange={(event) => setSettings((prev) => prev ? { ...prev, alreadyRespondedMessage: event.target.value } : prev)} />
              </Field>
              <Field label="Mensaje de enlace expirado">
                <Input value={settings.expiredMessage} onChange={(event) => setSettings((prev) => prev ? { ...prev, expiredMessage: event.target.value } : prev)} />
              </Field>
              <div className="flex justify-end">
                <button type="submit" disabled={saving} className="rounded-md bg-ui-accent px-4 py-2 text-sm font-medium text-ui-bg hover:bg-ui-accent-hover disabled:opacity-50">
                  {saving ? 'Guardando…' : 'Guardar ajustes'}
                </button>
              </div>
            </div>
          </form>
        </Modal>
      )}
    </PageContainer>
  );
}

function StatCard({ title, value, detail }: { title: string; value: string; detail: string }) {
  return (
    <article className="rounded-xl border border-ui-border bg-ui-surface p-4">
      <p className="text-sm text-ui-text-muted">{title}</p>
      <p className="mt-2 text-3xl font-semibold text-ui-text">{value}</p>
      <p className="mt-1 text-sm text-ui-text-subtle">{detail}</p>
    </article>
  );
}

function StatusPill({ label }: { label: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-amber-400/15 text-amber-300',
    sent: 'bg-sky-400/15 text-sky-300',
    opened: 'bg-indigo-400/15 text-indigo-300',
    responded: 'bg-emerald-400/15 text-emerald-300',
    expired: 'bg-ui-border text-ui-text-muted',
    new: 'bg-amber-400/15 text-amber-300',
    reviewed: 'bg-emerald-400/15 text-emerald-300',
    archived: 'bg-ui-border text-ui-text-muted',
    draft: 'bg-sky-400/15 text-sky-300',
    approved: 'bg-emerald-400/15 text-emerald-300',
    rejected: 'bg-rose-400/15 text-rose-300',
  };
  return <span className={`rounded-full px-2 py-1 text-sm font-medium ${styles[label] ?? 'bg-ui-border text-ui-text-muted'}`}>{label}</span>;
}

function EmptyState({ text }: { text: string }) {
  return <p className="rounded-lg border border-dashed border-ui-border px-4 py-6 text-center text-sm text-ui-text-muted">{text}</p>;
}
